import type { Map } from "leaflet";
import { CURRENT_LOCATION_PANE_NAME } from "../constants/pane";

/**
 * Leaflet で表示している地図 DOM を HTMLCanvasElement に変換するユーティリティ。
 *
 * Leaflet の各ペイン (tilePane / overlayPane / drawnShapesPane / markerPane 等)
 * を順に走査し、画像 / Canvas / SVG / HTML 要素をそれぞれ適切な手段で
 * 出力 Canvas に描き込む。React/Hook に依存しない純関数として提供する。
 */

/**
 * 画像要素の読み込み完了を待つ。
 * Canvas へ描画する前に必ず呼ぶこと（未ロード状態で描画すると空白になる）。
 *
 * @param img 読み込みを待つ画像要素
 * @param src 新しく設定するソース URL（省略時は既存の src を使う）
 */
export const waitForImageLoad = async (
  img: HTMLImageElement,
  src?: string
): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    // 既に完了していて新しい src も指定されていなければ即 resolve。
    if (img.complete && img.naturalWidth > 0 && !src) {
      resolve();
      return;
    }
    const cleanup = (): void => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
    const handleLoad = (): void => {
      cleanup();
      resolve();
    };
    const handleError = (): void => {
      cleanup();
      reject(new Error("failed to load image"));
    };
    img.addEventListener("load", handleLoad);
    img.addEventListener("error", handleError);
    if (src) {
      img.src = src;
    }
  });
};

/**
 * 要素の実効不透明度（自身から root までの祖先の opacity の積）を返す。
 * Leaflet はレイヤーの透過を要素自身ではなくコンテナ（leaflet-layer div や
 * ペイン）の CSS opacity で表現するため、drawImage 時にこれを掛けないと
 * 半透明レイヤー（役所地番地図の対応エリア等）が不透明で焼かれてしまう。
 *
 * ただしタイル要素（.leaflet-tile）自身の opacity は無視する。Leaflet は
 * タイルロード完了直後に tile 要素の inline opacity を 0 にして約 200ms かけて
 * 1 へフェードインさせるため、キャプチャがフェード中に走ると中途半端な
 * opacity が焼き込まれて出力全体が白っぽくなる。レイヤーの意図的な透過
 * （GridLayer.setOpacity）はコンテナ div 側に載るので、タイル自身の opacity を
 * 無視しても失われない。
 *
 * @param includeSelf 自身の opacity を積に含めるか。SVG / HTML のシリアライズ
 * 経路は computed style を clone に焼き込む（自身の opacity 込み）ため、
 * false を渡して祖先分のみを返す。
 */
const getEffectiveOpacity = (
  element: Element,
  root: Element,
  includeSelf = true
): number => {
  let opacity = 1;
  let current: Element | null = includeSelf ? element : element.parentElement;
  if (!includeSelf && element === root) {
    current = null;
  }
  while (current !== null) {
    const value = Number.parseFloat(window.getComputedStyle(current).opacity);
    if (Number.isFinite(value) && !current.classList.contains("leaflet-tile")) {
      opacity *= value;
    }
    if (current === root) break;
    current = current.parentElement;
  }
  return opacity;
};

/**
 * 要素を deep clone し、計算済みスタイルをインラインスタイルとして埋め込む。
 * SVG / HTML をシリアライズするときに CSS クラスや外部スタイルシートは
 * 適用されないため、ここで computed style を焼き込んでおく。
 */
const cloneElementWithInlineStyles = (element: Element): Element => {
  const clone = element.cloneNode(true) as Element;

  const copyStyles = (source: Element, target: Element): void => {
    const computed = window.getComputedStyle(source);
    if (target instanceof HTMLElement || target instanceof SVGElement) {
      Array.from(computed).forEach((property) => {
        target.style.setProperty(
          property,
          computed.getPropertyValue(property),
          computed.getPropertyPriority(property)
        );
      });
      // Leaflet が乗せる translate / scale はシリアライズ後の描画位置を
      // ずらしてしまうため、ここでリセットする。
      if (computed.transform && computed.transform !== "none") {
        target.style.setProperty("transform", "none");
      }
    }
    Array.from(source.childNodes).forEach((child, index) => {
      const targetChild = target.childNodes[index];
      if (child instanceof Element && targetChild instanceof Element) {
        copyStyles(child, targetChild);
      }
    });
  };

  copyStyles(element, clone);
  return clone;
};

/**
 * SVG マークアップ文字列を HTMLImageElement として読み込む。
 * Canvas に直接 SVG は描画できないため data URL 経由で Image にする。
 */
const loadSvgMarkupAsImage = async (
  serialized: string
): Promise<HTMLImageElement> =>
  await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject(new Error("failed to convert svg to image"));
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      serialized
    )}`;
  });

export interface CreateMapCanvasResult {
  /** 出力 Canvas（devicePixelRatio スケール済み） */
  canvas: HTMLCanvasElement;
  /** CORS 制約で取得できなかった画像のオリジン一覧 */
  skippedSources: string[];
  /** drawImage に失敗した Canvas の識別子一覧 */
  skippedCanvases: string[];
}

/**
 * 地図 DOM を Canvas へ書き出す。
 *
 * devicePixelRatio を考慮して高解像度ディスプレイでも鮮明な画像を生成する。
 * 描画順序は Leaflet の標準ペイン順 (tile → overlay → MapLibre canvas →
 * drawnShapes → shadow → marker → tooltip → popup → map) を再現している。
 */
export const createMapCanvas = async (
  mapInstance: Map
): Promise<CreateMapCanvasResult> => {
  const size = mapInstance.getSize();
  const mapContainer = mapInstance.getContainer();
  const mapRect = mapContainer.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;

  const canvas = document.createElement("canvas");
  canvas.width = size.x * dpr;
  canvas.height = size.y * dpr;

  const skippedSources = new Set<string>();
  const skippedCanvases = new Set<string>();
  const drawnElements = new Set<Element>();

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("canvas context is not available");
  }
  // dpr 倍にスケールすることで、以降は論理ピクセルで描画指示できる。
  context.scale(dpr, dpr);
  // 背景を白で塗りつぶす（透明だと PDF 化したときに問題が出るため）。
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, size.x, size.y);

  /**
   * 画面座標を地図コンテナ内の相対座標に変換する。
   * getBoundingClientRect はビューポート基準なので、地図左上を原点にし直す。
   */
  const toRelativeRect = (rect: DOMRect): DOMRect => {
    const { x, y, width, height } = rect;
    return new DOMRect(x - mapRect.left, y - mapRect.top, width, height);
  };

  /**
   * 要素の実効不透明度（祖先の opacity 込み）を globalAlpha に適用して描画する。
   * 半透明レイヤー（setOpacity されたタイルレイヤーや透過ペイン）を
   * 画面表示と同じ濃度で出力するために必要。
   */
  const drawWithElementOpacity = (
    element: Element,
    draw: () => void,
    includeSelf = true
  ): void => {
    const opacity = getEffectiveOpacity(element, mapContainer, includeSelf);
    if (opacity >= 1) {
      draw();
      return;
    }
    context.save();
    context.globalAlpha = opacity;
    draw();
    context.restore();
  };

  const drawImageElement = async (img: HTMLImageElement): Promise<void> => {
    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const src = img.currentSrc || img.src;
    if (!src) return;

    const url = new URL(src, window.location.href);
    const isDataOrBlob = url.protocol === "data:" || url.protocol === "blob:";
    const isSameOrigin = url.origin === window.location.origin || isDataOrBlob;

    const draw = (image: HTMLImageElement): void => {
      const relativeRect = toRelativeRect(rect);
      drawWithElementOpacity(img, () => {
        context.drawImage(
          image,
          relativeRect.x,
          relativeRect.y,
          rect.width,
          rect.height
        );
      });
    };

    if (isSameOrigin) {
      await waitForImageLoad(img);
      draw(img);
      return;
    }

    // クロスオリジン画像は fetch → Blob URL 経由で描画を試みる。
    // サーバー側 CORS 設定次第で失敗するため、失敗時はスキップ記録のみ残す。
    try {
      const response = await fetch(src, { mode: "cors" });
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const clonedImage = new Image();
      clonedImage.crossOrigin = "anonymous";
      await waitForImageLoad(clonedImage, blobUrl);
      draw(clonedImage);
      URL.revokeObjectURL(blobUrl);
    } catch (_error) {
      skippedSources.add(url.origin);
    }
  };

  const drawCanvasElement = (canvasElement: HTMLCanvasElement): void => {
    const rect = canvasElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const relativeRect = toRelativeRect(rect);
    try {
      drawWithElementOpacity(canvasElement, () => {
        context.drawImage(
          canvasElement,
          relativeRect.x,
          relativeRect.y,
          rect.width,
          rect.height
        );
      });
    } catch (_error) {
      // tainted canvas / WebGL preserveDrawingBuffer 等で失敗した場合は記録のみ。
      const id =
        canvasElement.className ||
        (canvasElement.getAttribute("data-layer") ?? "unknown-canvas");
      skippedCanvases.add(String(id));
    }
  };

  const drawSvgElement = async (svg: SVGSVGElement): Promise<void> => {
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const clonedSvg = cloneElementWithInlineStyles(svg) as SVGSVGElement;
    const serialized = new XMLSerializer().serializeToString(clonedSvg);
    const img = await loadSvgMarkupAsImage(serialized);
    const relativeRect = toRelativeRect(rect);
    // 自身の opacity は cloneElementWithInlineStyles で clone に焼き込み済み。
    // globalAlpha にも掛けると二重適用になるため祖先分のみ渡す。
    drawWithElementOpacity(
      svg,
      () => {
        context.drawImage(
          img,
          relativeRect.x,
          relativeRect.y,
          rect.width,
          rect.height
        );
      },
      false
    );
  };

  const drawHtmlElement = async (element: HTMLElement): Promise<void> => {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const clonedElement = cloneElementWithInlineStyles(element) as HTMLElement;
    clonedElement.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
    const serialized = new XMLSerializer().serializeToString(clonedElement);
    const wrapped = `<svg xmlns="http://www.w3.org/2000/svg" width="${rect.width}" height="${rect.height}"><foreignObject width="100%" height="100%">${serialized}</foreignObject></svg>`;
    const img = await loadSvgMarkupAsImage(wrapped);
    const relativeRect = toRelativeRect(rect);
    // SVG 経路と同様、自身の opacity は clone に焼き込み済みなので祖先分のみ。
    drawWithElementOpacity(
      element,
      () => {
        context.drawImage(
          img,
          relativeRect.x,
          relativeRect.y,
          rect.width,
          rect.height
        );
      },
      false
    );
  };

  const renderElement = async (element: Element): Promise<void> => {
    if (drawnElements.has(element)) return;
    drawnElements.add(element);

    if (element instanceof HTMLImageElement) {
      await drawImageElement(element);
      return;
    }
    if (element instanceof HTMLCanvasElement) {
      // MapLibre の canvas は描画順序の都合で後段でまとめて処理する。
      if (element.classList.contains("maplibregl-canvas")) {
        return;
      }
      drawCanvasElement(element);
      return;
    }
    if (element instanceof SVGSVGElement) {
      await drawSvgElement(element);
      return;
    }

    if (element instanceof HTMLElement) {
      const style = window.getComputedStyle(element);
      const hasBackground =
        style.backgroundColor &&
        style.backgroundColor !== "rgba(0, 0, 0, 0)" &&
        style.backgroundColor !== "transparent";
      const hasBorder =
        style.borderStyle &&
        style.borderStyle !== "none" &&
        style.borderWidth !== "0px";
      const hasBoxShadow = style.boxShadow && style.boxShadow !== "none";
      const shouldRenderSelf =
        element.children.length === 0 ||
        hasBackground ||
        hasBorder ||
        hasBoxShadow;

      if (shouldRenderSelf) {
        await drawHtmlElement(element);
        return;
      }
    }

    for (const child of Array.from(element.children)) {
      await renderElement(child);
    }
  };

  const panes = mapInstance.getPanes();
  /**
   * Leaflet の描画レイヤー順序。MapLibre の canvas は drawnShapes / marker より
   * 下に描画したいので、Leaflet ペインの間に挟み込む。
   */
  const paneOrderBelowMapLibre: Array<keyof typeof panes> = [
    "tilePane",
    "overlayPane",
  ];
  const paneOrderAboveMapLibre: Array<keyof typeof panes> = [
    "drawnShapesPane",
    "shadowPane",
    "markerPane",
    CURRENT_LOCATION_PANE_NAME as keyof typeof panes,
    "tooltipPane",
    "popupPane",
    "mapPane",
  ];

  const renderPaneChildren = async (
    paneName: keyof typeof panes
  ): Promise<void> => {
    const pane = panes[paneName];
    if (!pane) return;

    const children = Array.from(pane.children);
    children.sort((a, b) => {
      const az = Number.parseFloat((a as HTMLElement).style.zIndex || "0");
      const bz = Number.parseFloat((b as HTMLElement).style.zIndex || "0");
      return az - bz;
    });

    for (const child of children) {
      await renderElement(child);
    }
  };

  for (const paneName of paneOrderBelowMapLibre) {
    await renderPaneChildren(paneName);
  }

  // MapLibre canvas は Leaflet ペインの外で管理されているため別途処理。
  const maplibreCanvases = mapInstance
    .getContainer()
    .querySelectorAll("canvas.maplibregl-canvas");
  maplibreCanvases.forEach((maplibreCanvas) => {
    if (maplibreCanvas instanceof HTMLCanvasElement) {
      drawCanvasElement(maplibreCanvas);
      drawnElements.add(maplibreCanvas);
    }
  });

  for (const paneName of paneOrderAboveMapLibre) {
    await renderPaneChildren(paneName);
  }

  return {
    canvas,
    skippedSources: Array.from(skippedSources),
    skippedCanvases: Array.from(skippedCanvases),
  };
};
